/**
 * Currency Utility Functions
 *
 * Provides utility functions for working with currency metadata using the Intl API.
 */

/** Cache for currency scales to avoid repeated Intl lookups */
const scaleCache = new Map<string, number>()

/**
 * Gets the number of decimal places (scale) for a given currency code.
 * Uses the Intl.NumberFormat API. Results are cached.
 *
 * @param currencyCode - ISO 4217 currency code (e.g., 'EUR', 'USD', 'JPY')
 * @throws Error if currency code is invalid
 *
 * @example
 * getCurrencyScale('EUR') // 2
 * getCurrencyScale('JPY') // 0
 * getCurrencyScale('KWD') // 3
 */
const getCurrencyScale = (currencyCode: string): number => {
  const cached = scaleCache.get(currencyCode)
  if (cached !== undefined) return cached

  const scale = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currencyCode
  }).resolvedOptions().maximumFractionDigits

  if (scale === undefined) {
    throw new Error(`Invalid currency code: ${currencyCode}`)
  }

  scaleCache.set(currencyCode, scale)
  return scale
}

/**
 * Proxy-based currency code accessor.
 * Validates currency exists via Intl API.
 *
 * @example
 * CURRENCY_CODE.EUR // 'EUR'
 * CURRENCY_CODE.USD // 'USD'
 */
const CURRENCY_CODE: Record<string, string> = new Proxy(
  {},
  {
    get(_, code: string): string {
      getCurrencyScale(code) // Validates currency exists
      return code
    }
  }
)

/**
 * Proxy-based currency scale accessor.
 * Returns decimal places for any valid ISO 4217 currency.
 *
 * @example
 * CURRENCY_SCALE.EUR // 2
 * CURRENCY_SCALE.JPY // 0
 * CURRENCY_SCALE.KWD // 3
 */
const CURRENCY_SCALE: Record<string, number> = new Proxy(
  {},
  {
    get(_, code: string): number {
      return getCurrencyScale(code)
    }
  }
)

export { CURRENCY_CODE, CURRENCY_SCALE, getCurrencyScale }
